import { useEffect, useRef } from "react";

const VIDEO_SRC =
  "https://public.readdy.ai/ai/video_res/019ece6a-6798-7181-9d8b-6f47c652c066.mp4";

// Full vertical viewport sweep maps to this many seconds of video timeline
const FULL_SWEEP_SECONDS = 4.5;

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const prevMouseY = useRef<number | null>(null);
  const targetTime = useRef(0);
  const lastSeekedTime = useRef(0);
  const isSeeking = useRef(false);
  const isVisible = useRef(true);
  const isDesktop = useRef(false);
  const settleTimer = useRef<number | null>(null);

  // Fully buffer the whole video first so seeks don't stall on network IO
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => {
      try {
        video.pause();
        video.currentTime = 0;
        targetTime.current = 0;
        lastSeekedTime.current = 0;
      } catch {
        /* noop */
      }
    };
    const handleSeeking = () => {
      isSeeking.current = true;
    };
    const handleSeeked = () => {
      isSeeking.current = false;
      lastSeekedTime.current = video.currentTime;
      // If user moved more while we were seeking, dispatch next one immediately
      maybeDispatchSeek();
    };

    video.addEventListener("loadedmetadata", handleLoaded);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoaded);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Key change: only fire ONE seek at a time, queue the latest target until prev resolves
  const maybeDispatchSeek = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isSeeking.current) return;
    const duration = video.duration;
    if (!duration || Number.isNaN(duration)) return;

    let next = targetTime.current;
    if (next < 0) next = 0;
    if (next > duration - 0.05) next = duration - 0.05;

    // Skip tiny moves to avoid pointless seek thrash
    if (Math.abs(next - lastSeekedTime.current) < 0.012) return;

    try {
      const fastSeek = (video as HTMLVideoElement & {
        fastSeek?: (t: number) => void;
      }).fastSeek;
      if (typeof fastSeek === "function") {
        fastSeek.call(video, next);
      } else {
        video.currentTime = next;
      }
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const updateMq = () => {
      isDesktop.current = mq.matches;
    };
    updateMq();
    mq.addEventListener("change", updateMq);
    return () => {
      mq.removeEventListener("change", updateMq);
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible.current = entry.isIntersecting;
        });
      },
      { threshold: 0.05 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDesktop.current || !isVisible.current) return;
      const video = videoRef.current;
      if (!video) return;

      const y = e.clientY;
      if (prevMouseY.current === null) {
        prevMouseY.current = y;
        return;
      }
      const deltaY = y - prevMouseY.current;
      prevMouseY.current = y;
      if (deltaY === 0) return;

      const duration = video.duration || FULL_SWEEP_SECONDS;
      const increment = (deltaY / window.innerHeight) * FULL_SWEEP_SECONDS;
      let next = targetTime.current + increment;
      if (next < 0) next = 0;
      if (next > duration - 0.05) next = duration - 0.05;
      targetTime.current = next;

      // Dispatch seek; if one is already running, handleSeeked will pick up the latest target
      maybeDispatchSeek();

      // When mouse stops, do one final precise seek
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
      }
      settleTimer.current = window.setTimeout(() => {
        const v = videoRef.current;
        if (!v) return;
        try {
          v.currentTime = targetTime.current;
        } catch {
          /* noop */
        }
      }, 90);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
        settleTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile: autoplay loop fallback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const mq = window.matchMedia("(min-width: 1024px)");

    const apply = () => {
      if (!mq.matches) {
        video.loop = true;
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        video.loop = false;
        try {
          video.pause();
        } catch {
          /* noop */
        }
      }
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen overflow-hidden"
    >
      {/* Background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
        src={VIDEO_SRC}
        muted
        playsInline
        preload="auto"
        disableRemotePlayback
      />

      {/* Content - middle left */}
      <div className="relative z-10 w-full min-h-screen flex items-center">
        <div className="w-full px-6 md:px-12 lg:px-20 pt-32 pb-20">
          <div className="max-w-2xl">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[1px] bg-white/60"></span>
              <span className="text-white/80 text-[11px] font-medium tracking-[0.3em] uppercase">
                New — 2026
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-display text-white text-6xl md:text-7xl lg:text-[112px] font-normal leading-[0.95] tracking-[-0.02em]">
              A tiny world
              <br />
              of <span className="italic">wonder</span>.
            </h1>

            {/* Subheadline */}
            <p className="mt-8 text-white/80 text-base md:text-[17px] font-light leading-relaxed max-w-md">
              Soft sculpted characters and collectible art — made to make every
              shelf a little happier.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href="#shop"
                className="group inline-flex items-center gap-3 bg-white hover:bg-[#f4d35e] text-neutral-900 text-[13px] font-semibold tracking-wide px-7 py-3.5 rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                Shop the Collection
                <span className="w-4 h-4 flex items-center justify-center transform transition-transform group-hover:translate-x-1">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
              <a
                href="#story"
                className="group inline-flex items-center gap-3 text-white text-[13px] font-medium tracking-wide whitespace-nowrap cursor-pointer"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-full border border-white/40 group-hover:border-white transition-all">
                  <i className="ri-play-fill text-sm"></i>
                </span>
                Watch our story
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hint */}
      <div className="absolute bottom-8 right-8 z-10 hidden lg:flex items-center gap-3 text-white/70 text-[10px] tracking-[0.3em] uppercase pointer-events-none">
        <span>Move</span>
        <span className="w-8 h-[1px] bg-white/40"></span>
        <i className="ri-arrow-up-down-line text-sm"></i>
      </div>
    </section>
  );
}